import { axiosInstance } from "@/config/apiClient";
import type {
	HipotecaTransicionCreate,
	HipotecaTransicionResponse,
	LiquidacionHipoteca,
	MortgageResponse,
	MortgagePaginated,
	MortgageFormData,
} from "../schemas/mortgage";

export const mortgagesApi = {
	// User-level: Get all mortgages across all properties
	getAllForUser: async (params?: {
		include_resumen?: boolean;
		estado?: string;
		limit?: number;
		skip?: number;
	}): Promise<MortgagePaginated> => {
		const response = await axiosInstance.get<MortgagePaginated>("/hipoteca", {
			params: {
				include_resumen: params?.include_resumen ?? true,
				...(params?.estado && { estado: params.estado }),
				...(params?.limit !== undefined && { limit: params.limit }),
				...(params?.skip !== undefined && { skip: params.skip }),
			},
		});
		return response.data;
	},

	// Property-level: Get all mortgages for a specific property
	getAll: async (
		propId: number,
		params?: {
			include_resumen?: boolean;
			estado?: string;
			limit?: number;
			skip?: number;
		},
	): Promise<MortgagePaginated> => {
		const response = await axiosInstance.get<MortgagePaginated>(
			`/propiedad/${propId}/hipoteca`,
			{
				params: {
					...(params?.include_resumen !== undefined && {
						include_resumen: params.include_resumen,
					}),
					...(params?.estado && { estado: params.estado }),
					...(params?.limit !== undefined && { limit: params.limit }),
					...(params?.skip !== undefined && { skip: params.skip }),
				},
			},
		);
		return response.data;
	},

	getCountForProperty: async (propId: number): Promise<number> => {
		const response = await mortgagesApi.getAll(propId, {
			include_resumen: false,
			limit: 1,
			skip: 0,
		});

		return response.pagination.total_count;
	},

	getById: async (
		propId: number,
		id: number,
		params?: { include_resumen?: boolean },
	): Promise<MortgageResponse> => {
		const response = await axiosInstance.get<MortgageResponse>(
			`/propiedad/${propId}/hipoteca/${id}`,
			{
				params: {
					...(params?.include_resumen !== undefined && {
						include_resumen: params.include_resumen,
					}),
				},
			},
		);
		return response.data;
	},

	getLiquidationEstimate: async (
		propId: number,
		id: number,
		params: { fecha_operacion: string; cuota_mes_pagada?: boolean },
	): Promise<LiquidacionHipoteca> => {
		const response = await axiosInstance.get<LiquidacionHipoteca>(
			`/propiedad/${propId}/hipoteca/${id}/liquidacion`,
			{ params },
		);
		return response.data;
	},

	create: async (
		propId: number,
		data: MortgageFormData,
	): Promise<MortgageResponse> => {
		const response = await axiosInstance.post<MortgageResponse>(
			`/propiedad/${propId}/hipoteca`,
			data,
		);
		return response.data;
	},

	update: async (
		propId: number,
		id: number,
		data: Partial<MortgageFormData>,
	): Promise<MortgageResponse> => {
		const response = await axiosInstance.patch<MortgageResponse>(
			`/propiedad/${propId}/hipoteca/${id}`,
			data,
		);
		return response.data;
	},

	transition: async (
		propId: number,
		id: number,
		data: HipotecaTransicionCreate,
	): Promise<HipotecaTransicionResponse> => {
		const response = await axiosInstance.post<HipotecaTransicionResponse>(
			`/propiedad/${propId}/hipoteca/${id}/transicion`,
			data,
		);
		return response.data;
	},

	delete: async (propId: number, id: number): Promise<void> => {
		await axiosInstance.delete(`/propiedad/${propId}/hipoteca/${id}`);
	},
};
